import type { FormikHelpers } from 'formik/dist/types'
import { reverse } from 'named-urls'
import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ConfirmModal, useModalContext } from '@fourthwall/components'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { PaymentMethodRequiredModal } from '@modules/Dashboard/modules/PaymentMethods'
import { useIsPaymentMethodModalRequiredMutation } from '@mutations/useIsPaymentMethodModalRequiredMutation'
import { routing } from '@utils/routing'

import { useOfferGiftCardCreationMutation } from '../../../../../Product/CreateProduct/CreateGiftCardProductButton/queries/useOfferGiftCardCreationMutation'
import { PaymentMethodRecommendedModal } from '../../../components/PaymentMethodRecommendedModal/PaymentMethodRecommendedModal'
import type { FormValues } from '../GiftCard.types'
import { useArchiveGiftCardGroupMutation } from '../queries/useArchiveGiftCardGroupMutation'
import { useArchiveGiftCardMutation } from '../queries/useArchiveGiftCardMutation'
import { useCreateGiftCardGroupMutation } from '../queries/useCreateGiftCardGroupMutation'
import { useCreateGiftCardMutation } from '../queries/useCreateGiftCardMutation'
import { useDeactivateGiftCardGroupMutation } from '../queries/useDeactivateGiftCardGroupMutation'
import { useDeactivateGiftCardMutation } from '../queries/useDeactivateGiftCardMutation'
import { useGiftCardGroupQuery } from '../queries/useGiftCardGroupQuery'
import { useGiftCardQuery } from '../queries/useGiftCardQuery'
import { useReactivateGiftCardGroupMutation } from '../queries/useReactivateGiftCardGroupMutation'
import { useReactivateGiftCardMutation } from '../queries/useReactivateGiftCardMutation'
import { useUpdateGiftCardGroupMutation } from '../queries/useUpdateGiftCardGroupMutation'
import { useUpdateGiftCardMutation } from '../queries/useUpdateGiftCardMutation'
import { getGiftCardTitle } from '../utils/getGiftCardTitle'
import useGiftCardTimeline from './useGiftCardTimeline'

export const useGiftCard = () => {
  const { giftCardId = '' } = useParams<'giftCardId'>()
  const navigate = useNavigate()
  const { currentShopQuery } = useCurrentShop()
  const { open } = useModalContext()
  const isCreating = !giftCardId
  const isGiftCardGroup = giftCardId.startsWith('gcg_')
  const { createGiftCardMutation } = useCreateGiftCardMutation()
  const { createGiftCardGroupMutation } = useCreateGiftCardGroupMutation()
  const { updateGiftCardMutation } = useUpdateGiftCardMutation()
  const { updateGiftCardGroupMutation } = useUpdateGiftCardGroupMutation()
  const { isPaymentMethodModalRequiredMutation } = useIsPaymentMethodModalRequiredMutation()
  const { deactivateGiftCardMutation } = useDeactivateGiftCardMutation()
  const { deactivateGiftCardGroupMutation } = useDeactivateGiftCardGroupMutation()
  const { reactivateGiftCardMutation } = useReactivateGiftCardMutation()
  const { reactivateGiftCardGroupMutation } = useReactivateGiftCardGroupMutation()
  const { archiveGiftCardMutation } = useArchiveGiftCardMutation()
  const { archiveGiftCardGroupMutation } = useArchiveGiftCardGroupMutation()
  const { offerGiftCardCreationMutation } = useOfferGiftCardCreationMutation({
    onSuccess: ({ id }) => {
      navigate(
        `${reverse(routing.products.giftCard.giftCardId.self, { giftCardId: id })}${location.search}`,
      )
    },
  })
  const { giftCardQuery, invalidateGiftCardQuery } = useGiftCardQuery([{ id: giftCardId }], {
    enabled: !isCreating && !isGiftCardGroup,
  })
  const { giftCardGroupQuery, invalidateGiftCardGroupQuery } = useGiftCardGroupQuery(
    [{ groupId: giftCardId }],
    {
      enabled: !isCreating && isGiftCardGroup,
    },
  )

  const giftCard = isCreating ? undefined : giftCardQuery.data || giftCardGroupQuery.data
  const isCreatedFromOrder =
    !isGiftCardGroup && (giftCardQuery.data?.origin?.type as string | undefined) === 'ORDER'

  const { timelineEvents, isFetching: isGiftCardTimelineFetching } = useGiftCardTimeline({
    giftCardId,
    isCreating,
    isGiftCardGroup,
  })

  const initialValues: FormValues = useMemo(() => {
    if (isCreating) {
      return {
        type: 'SINGLE',
        code: '',
        codes: [],
        amount: '',
      }
    }

    return {
      type: isGiftCardGroup ? 'MULTIPLE' : 'SINGLE',
      code: giftCardQuery.data?.code || '',
      codes: giftCardGroupQuery.data?.giftCards?.map((giftCard) => giftCard.code) || [],
      amount:
        giftCardQuery.data?.balance.initial?.value?.toFixed(2) ||
        giftCardGroupQuery.data?.giftCards?.[0]?.balance.initial?.value?.toFixed(2) ||
        '',
    }
  }, [isCreating, isGiftCardGroup, giftCardQuery.data, giftCardGroupQuery.data])

  const handleCreateSuccess = (giftCardId: string) => {
    const path = reverse(routing.promotions.giftCards.giftCard.self, {
      giftCardId,
    })

    navigate(path)
  }

  const createMultipleGiftCards = async (values: FormValues) => {
    const data = await createGiftCardGroupMutation.mutateAsync([
      {
        codes: values.codes,
        amount: {
          value: Number(values.amount),
          currency: currentShopQuery.data?.settings.currency!,
        },
      },
    ])

    handleCreateSuccess(data.giftCardGroupId)
  }

  const createSingleGiftCard = async (values: FormValues) => {
    const data = await createGiftCardMutation.mutateAsync([
      {
        code: values.code,
        amount: {
          value: Number(values.amount),
          currency: currentShopQuery.data?.settings.currency!,
        },
      },
    ])

    handleCreateSuccess(data.giftCardId)
  }

  const createGiftCards = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    formikHelpers.resetForm({ values })

    if (values.type === 'SINGLE') {
      createSingleGiftCard(values)

      return
    }

    createMultipleGiftCards(values)
  }

  const addPaymentMethod = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    open(PaymentMethodRequiredModal, {
      title: 'Payment method required',
      subtitle: (
        <>
          <p>
            Gift cards can incur costs when the value of the gift card exceeds your profit. You need
            to add a payment method to your account before you can proceed.
          </p>
          <p>
            Your payment method will only be charged if your account profits are not sufficient to
            cover the balance.
          </p>
        </>
      ),
      onSuccess: () => {
        createGiftCards(values, formikHelpers)
      },
    })
  }

  const handlePaymentModal = (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>,
    isPaymentMethodModalRequired: boolean,
  ) => {
    if (!isPaymentMethodModalRequired) {
      createGiftCards(values, formikHelpers)

      return
    }

    open(PaymentMethodRecommendedModal, {
      title: 'Do you want to allow promotions that result in negative profit?',
      confirmLabel: 'Create promotion',
      skipDisabled: true,
      onConfirm: (paymentMethodChoice) => {
        if (paymentMethodChoice === 'SKIP') {
          createGiftCards(values, formikHelpers)

          return
        }
        addPaymentMethod(values, formikHelpers)
      },
    })
  }

  const handleUpdateGiftCard = async (values: FormValues) => {
    if (values.type === 'SINGLE') {
      await updateGiftCardMutation.mutateAsync([{ id: giftCardId }, {}])
    }

    if (values.type === 'MULTIPLE') {
      await updateGiftCardGroupMutation.mutateAsync([{ groupId: giftCardId }, {}])
    }

    invalidateGiftCardQuery()
    invalidateGiftCardGroupQuery()
  }

  const canPerformAction = (action: 'DEACTIVATION' | 'ARCHIVIZATION' | 'REACTIVATION') => {
    return (
      giftCardQuery.data?.availableActions.includes(action) ||
      giftCardGroupQuery.data?.availableActions.includes(action)
    )
  }

  const onDeactivate = async () => {
    if (isGiftCardGroup) {
      await deactivateGiftCardGroupMutation.mutateAsync([{ groupId: giftCardId }])
      invalidateGiftCardGroupQuery()
    } else {
      await deactivateGiftCardMutation.mutateAsync([{ id: giftCardId }])
      invalidateGiftCardQuery()
    }
  }

  const onReactivate = async () => {
    if (isGiftCardGroup) {
      await reactivateGiftCardGroupMutation.mutateAsync([{ groupId: giftCardId }])
      invalidateGiftCardGroupQuery()
    } else {
      await reactivateGiftCardMutation.mutateAsync([{ id: giftCardId }])
      invalidateGiftCardQuery()
    }
  }

  const onArchiveConfirm = async () => {
    if (isGiftCardGroup) {
      await archiveGiftCardGroupMutation.mutateAsync([{ groupId: giftCardId }])
      invalidateGiftCardGroupQuery()
    } else {
      await archiveGiftCardMutation.mutateAsync([{ id: giftCardId }])
      invalidateGiftCardQuery()
    }

    navigate(reverse(routing.promotions.giftCards.self))
  }

  const onArchive = async () => {
    if (!giftCard) return

    open(ConfirmModal, {
      title: `Are you sure that you want to remove ${getGiftCardTitle(giftCard)} gift card?`,
      confirmLabel: 'Delete',
      confirmAppearance: 'destructive',
      onConfirm: onArchiveConfirm,
    })
  }

  const handleCreateGiftCardProduct = async () => {
    await offerGiftCardCreationMutation.mutateAsync([])
  }

  const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    if (isCreating) {
      isPaymentMethodModalRequiredMutation.mutate([], {
        onSuccess: (data) => {
          handlePaymentModal(values, formikHelpers, data.isPaymentMethodModalRequired)
        },
      })

      return
    }

    handleUpdateGiftCard(values)
  }

  return {
    giftCard,
    timelineEvents,
    initialValues,
    isCreating,
    isCreatedFromOrder,
    isFetching:
      giftCardQuery.isFetching ||
      giftCardGroupQuery.isFetching ||
      currentShopQuery.isFetching ||
      isGiftCardTimelineFetching,
    isSubmitting:
      createGiftCardMutation.isLoading ||
      createGiftCardGroupMutation.isLoading ||
      updateGiftCardMutation.isLoading ||
      updateGiftCardGroupMutation.isLoading ||
      deactivateGiftCardMutation.isLoading ||
      deactivateGiftCardGroupMutation.isLoading ||
      reactivateGiftCardMutation.isLoading ||
      reactivateGiftCardGroupMutation.isLoading ||
      archiveGiftCardMutation.isLoading ||
      archiveGiftCardGroupMutation.isLoading ||
      offerGiftCardCreationMutation.isLoading,
    onSubmit: handleSubmit,
    onDeactivate: canPerformAction('DEACTIVATION') ? onDeactivate : undefined,
    onReactivate: canPerformAction('REACTIVATION') ? onReactivate : undefined,
    onArchive: canPerformAction('ARCHIVIZATION') ? onArchive : undefined,
    onCreateGiftCardProduct: handleCreateGiftCardProduct,
  }
}
